import {
  getPatternDefForPage,
  updatePatternDef,
} from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/routerConfig'
import {
  getDatasetConfig,
  setDatasetConfig,
} from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/router-config/patternDef'
import type {
  EditorSDK,
  PatternConfig,
  ReadWriteMode,
} from '@wix/wix-data-client-common-standalone'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'

export type CombinedRouterDatasetConfig = {
  dataset: Omit<PatternConfig, 'seoV2' | 'private' | 'lowercase'> & {
    readWriteType: ReadWriteMode
    collectionName: string
  }
}

export const getRouterDatasetConfig = async function (
  editorSdkProxy: EditorSDK,
  controllerRef: ComponentRef,
  controllerConfig: CombinedRouterDatasetConfig | undefined,
) {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: controllerRef,
  })
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const { config: routerConfig, pages: routerPages } =
    await editorSdkProxy.routers.get({ routerRef })
  const { def: patternDef } = getPatternDefForPage(
    routerConfig,
    routerPages,
    pageRef,
  )
  const datasetConfigFromRouter = getDatasetConfig(patternDef)
  return {
    dataset: {
      ...controllerConfig?.dataset,
      collectionName: datasetConfigFromRouter.collection,
      filter: datasetConfigFromRouter.filter,
      sort: datasetConfigFromRouter.sort,
      pageSize: datasetConfigFromRouter.pageSize,
      includes: datasetConfigFromRouter.includes,
      cursor: datasetConfigFromRouter.cursor,
      nested: datasetConfigFromRouter.nested,
    },
  }
}

export const saveRouterDatasetConfig = async function (
  editorSdkProxy: EditorSDK,
  controllerRef: ComponentRef,
  config: CombinedRouterDatasetConfig,
) {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: controllerRef,
  })
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const currentRouterData = await editorSdkProxy.routers.get({ routerRef })
  const { pattern, def: currentPatternDef } = getPatternDefForPage(
    currentRouterData.config,
    currentRouterData.pages,
    pageRef,
  )

  const { dataset } = config

  const newPatternDef = setDatasetConfig(currentPatternDef, {
    collection: dataset.collectionName,
    filter: dataset.filter,
    sort: dataset.sort,
    pageSize: dataset.pageSize,
    includes: dataset.includes,
    lowercase: currentPatternDef.config.lowercase,
    cursor: dataset.cursor,
    nested: dataset.nested,
  })

  const newRouterConfig = updatePatternDef(
    currentRouterData.config,
    pattern,
    newPatternDef,
  )
  const newControllerConfig = {
    dataset: {
      readWriteType: dataset.readWriteType,
    },
  }

  await Promise.all([
    editorSdkProxy.routers.update({ routerRef, config: newRouterConfig }),
    editorSdkProxy.controllers.saveConfiguration({
      controllerRef,
      config: newControllerConfig,
    }),
  ])
}
